import { notification } from 'antd';
import { useState } from 'react';
import { useLocation } from 'react-router';

import { userApi } from '@/api/user.api';
import useAppNavigate from '@/shared/hooks/useAppNavigation';
import { ROUTES } from '@/shared/routes';
import { useAppDispatch } from '@/store';
import { setIsAuth } from '@/store/auth.slice';

import type { IFormInput } from '../types/form-types';

const clearCacheStorage = async (): Promise<void> => {
  if (!('caches' in window)) return;
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
};

const unregisterServiceWorkers = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((reg) => reg.unregister()));
};

const useLogin = () => {
  const location = useLocation();
  const { navigate } = useAppNavigate();
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (data: IFormInput) => {
    setIsLoading(true);

    try {
      await userApi.login({
        email: data.email,
        password: data.password
      });

      let startPath = ROUTES.leasingScoring;
      const menu = await userApi.getUserMenu();
      if (menu && menu.length > 0) {
        const sortedMenu = menu.toSorted((a, b) => a.position - b.position);
        if (sortedMenu.length > 0) {
          startPath = sortedMenu[0].code;
        } else {
          notification.warning({
            title: 'Нет доступных разделов',
            description:
              'Пожалуйста, обратитесь к администратору для получения доступа'
          });
          setIsLoading(false);
          return;
        }
      } else {
        notification.error({
          title: 'Ошибка получения разделов',
          description: 'Пожалуйста, попробуйте позже'
        });
        setIsLoading(false);
        return;
      }

      dispatch(setIsAuth(true));

      // Очищаем кеш и сервисы работы, запрашиваем новый index.html без кэша
      await Promise.all([
        fetch(window.location.origin, { cache: 'reload' }),
        clearCacheStorage(),
        unregisterServiceWorkers()
      ]);

      if (location.state?.savedUrl)
        navigate(location.state.savedUrl, { replace: true });
      else navigate(startPath);
      setTimeout(() => window.location.reload(), 0);
    } catch (err) {
      const error = err as Error;
      notification.error({
        title: 'Ошибка авторизации',
        description: error.message
      });
    }

    setIsLoading(false);
  };

  return { isLoading, handleLogin };
};

export default useLogin;
